import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { bookingApi, paymentApi, promoApi, reviewApi } from '../lib/api';
import {
  Calendar, Clock, DollarSign, FileText, Loader2,
  CheckCircle, XCircle, AlertCircle, CreditCard, Plus,
  Receipt, Trash2, Check, X, Wallet, Tag, Star, Flame
} from 'lucide-react';
import { format } from 'date-fns';

// PayPal icon component
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .632-.544h6.964c2.327 0 4.14.629 5.392 1.872.596.589 1.017 1.274 1.255 2.04.254.815.313 1.749.177 2.776-.019.132-.041.265-.067.4-.452 2.433-1.517 4.246-3.177 5.396-1.562 1.084-3.558 1.634-5.932 1.634h-1.2a.641.641 0 0 0-.633.544l-.848 5.499zm6.112-15.917H8.676l-1.18 7.645h2.513c1.79 0 3.247-.359 4.335-1.068 1.144-.747 1.845-1.867 2.082-3.331.24-1.553-.107-2.666-.892-3.347-.682-.592-1.787-.899-3.346-.899z"/>
  </svg>
);

const Bookings = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [contractContent, setContractContent] = useState('');
  const [contractLoading, setContractLoading] = useState(false);
  const [contractType, setContractType] = useState(null); // 'ai' or 'template'
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Payment method selection
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('stripe');
  
  // Line items state
  const [feesDialogOpen, setFeesDialogOpen] = useState(false);
  const [selectedBookingForFees, setSelectedBookingForFees] = useState(null);
  const [bookingFees, setBookingFees] = useState({ fees: [], user_role: '', pending_count: 0, approved_total: 0 });
  const [newFee, setNewFee] = useState({ name: '', amount: '', description: '' });
  const [addingFee, setAddingFee] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  
  // Tip state
  const [tipAmount, setTipAmount] = useState('');
  const [tipAtCheckout, setTipAtCheckout] = useState(true);
  
  // Review state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedBookingForReview, setSelectedBookingForReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewedBookings, setReviewedBookings] = useState(new Set());

  useEffect(() => {
    loadBookings();
    loadPaymentMethods();
    checkPaymentStatus();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const response = await paymentApi.getPaymentMethods();
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await bookingApi.getMy();
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    const paypalSuccess = searchParams.get('paypal_success');
    const paymentId = searchParams.get('PayerID') ? searchParams.get('paymentId') : null;
    const payerId = searchParams.get('PayerID');
    
    // Handle Stripe return
    if (sessionId && status === 'success') {
      try {
        const response = await paymentApi.checkStatus(sessionId);
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Booking confirmed.');
          loadBookings();
        }
      } catch (error) {
        console.error('Failed to check payment:', error);
      }
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled');
    }
    
    // Handle PayPal return
    if (paypalSuccess === 'true' && payerId) {
      // Get paymentId from URL
      const urlPaymentId = searchParams.get('paymentId');
      if (urlPaymentId) {
        try {
          const response = await paymentApi.executePayPalPayment(urlPaymentId, payerId);
          if (response.data.status === 'success') {
            toast.success('PayPal payment successful! Booking confirmed.');
            loadBookings();
          }
        } catch (error) {
          console.error('Failed to execute PayPal payment:', error);
          toast.error('Failed to complete PayPal payment');
        }
      }
    } else if (searchParams.get('paypal_cancelled') === 'true') {
      toast.info('PayPal payment was cancelled');
    }
  };

  const openPaymentDialog = (booking) => {
    setSelectedBookingForPayment(booking);
    setSelectedPaymentMethod(paymentMethods.length > 0 ? paymentMethods[0].id : '');
    setPromoCode('');
    setAppliedPromo(null);
    setPromoError('');
    setTipAmount('');
    setTipAtCheckout(true);
    setPaymentDialogOpen(true);
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim() || !selectedBookingForPayment) return;
    
    setPromoLoading(true);
    setPromoError('');
    
    try {
      const response = await promoApi.apply(promoCode, selectedBookingForPayment.booking_id);
      setAppliedPromo(response.data);
      // Update the booking in state with new total
      setSelectedBookingForPayment({
        ...selectedBookingForPayment,
        total_price: response.data.new_total,
        promo_code: response.data.code,
        discount_amount: response.data.discount_applied
      });
      toast.success(`Promo code applied! You saved $${response.data.discount_applied.toFixed(2)}`);
    } catch (error) {
      setPromoError(error.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedBookingForPayment) return;
    
    setPaymentLoading(true);
    try {
      if (selectedPaymentMethod === 'stripe') {
        const response = await paymentApi.createCheckout({
          booking_id: selectedBookingForPayment.booking_id,
          origin_url: window.location.origin
        });
        window.location.href = response.data.url;
      } else if (selectedPaymentMethod === 'paypal') {
        const response = await paymentApi.createPayPalOrder({
          booking_id: selectedBookingForPayment.booking_id,
          origin_url: window.location.origin
        });
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setPaymentLoading(false);
    }
  };

  const handlePaymentLegacy = async (booking) => {
    setPaymentLoading(true);
    try {
      const response = await paymentApi.createCheckout({
        booking_id: booking.booking_id,
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate payment');
      setPaymentLoading(false);
    }
  };

  const handleGenerateContract = async (booking, useTemplate = false) => {
    setSelectedBooking(booking);
    setContractLoading(true);
    setContractDialogOpen(true);
    try {
      const response = await bookingApi.generateContract(booking.booking_id, useTemplate);
      setContractContent(response.data.contract);
      setContractType(response.data.type || (useTemplate ? 'template' : 'ai'));
    } catch (error) {
      toast.error('Failed to generate contract');
    } finally {
      setContractLoading(false);
    }
  };
  
  const openContractOptions = (booking) => {
    setSelectedBooking(booking);
    setContractContent('');
    setContractType(null);
    setContractDialogOpen(true);
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      await bookingApi.updateStatus(bookingId, status);
      toast.success(`Booking ${status}`);
      loadBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  // Review Functions
  const openReviewDialog = (booking) => {
    setSelectedBookingForReview(booking);
    setReviewRating(0);
    setReviewText('');
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (reviewText && reviewText.length > 500) {
      toast.error('Review text cannot exceed 500 characters');
      return;
    }
    
    setReviewLoading(true);
    try {
      await reviewApi.create({
        booking_id: selectedBookingForReview.booking_id,
        rating: reviewRating,
        text: reviewText || null
      });
      toast.success('Review submitted! Thank you for your feedback.');
      setReviewedBookings(prev => new Set([...prev, selectedBookingForReview.booking_id]));
      setReviewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  // Custom Line Items Functions
  const openFeesDialog = async (booking) => {
    setSelectedBookingForFees(booking);
    setFeesDialogOpen(true);
    try {
      const response = await bookingApi.getFees(booking.booking_id);
      setBookingFees(response.data);
    } catch (error) {
      toast.error('Failed to load fees');
    }
  };

  const handleAddFee = async () => {
    if (!newFee.name || !newFee.amount) {
      toast.error('Please enter a name and amount');
      return;
    }
    setAddingFee(true);
    try {
      await bookingApi.addFee(selectedBookingForFees.booking_id, {
        name: newFee.name,
        amount: parseFloat(newFee.amount),
        description: newFee.description
      });
      toast.success('Fee added - waiting for approval');
      setNewFee({ name: '', amount: '', description: '' });
      // Reload fees
      const response = await bookingApi.getFees(selectedBookingForFees.booking_id);
      setBookingFees(response.data);
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add fee');
    } finally {
      setAddingFee(false);
    }
  };

  const handleApproveFee = async (feeId, approved) => {
    try {
      await bookingApi.approveFee(selectedBookingForFees.booking_id, feeId, approved);
      toast.success(`Fee ${approved ? 'approved' : 'rejected'}`);
      // Reload fees
      const response = await bookingApi.getFees(selectedBookingForFees.booking_id);
      setBookingFees(response.data);
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fee');
    }
  };

  const handleRemoveFee = async (feeId) => {
    try {
      await bookingApi.removeFee(selectedBookingForFees.booking_id, feeId);
      toast.success('Fee removed');
      // Reload fees
      const response = await bookingApi.getFees(selectedBookingForFees.booking_id);
      setBookingFees(response.data);
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove fee');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'pending': return <AlertCircle className="h-5 w-5 text-yellow-400" />;
      case 'completed': return <CheckCircle className="h-5 w-5 text-blue-400" />;
      case 'cancelled': return <XCircle className="h-5 w-5 text-red-400" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getFeeStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      default: return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  const filterBookings = (status) => {
    if (status === 'all') return bookings;
    return bookings.filter(b => b.status === status);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isTalent = user?.user_type === 'talent';

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="bookings-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            My Bookings
          </h1>
          <p className="text-muted-foreground">
            {isTalent ? 'Manage your client bookings' : 'Track your talent bookings'}
          </p>
        </div>

        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="bg-muted/50 rounded-xl p-1">
            <TabsTrigger value="all" className="rounded-lg">All</TabsTrigger>
            <TabsTrigger value="pending" className="rounded-lg">Pending</TabsTrigger>
            <TabsTrigger value="confirmed" className="rounded-lg">Confirmed</TabsTrigger>
            <TabsTrigger value="completed" className="rounded-lg">Completed</TabsTrigger>
          </TabsList>

          {['all', 'pending', 'confirmed', 'completed'].map(tab => (
            <TabsContent key={tab} value={tab} className="space-y-4">
              {filterBookings(tab).length > 0 ? (
                filterBookings(tab).map(booking => (
                  <Card key={booking.booking_id} className="overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="p-3 rounded-xl bg-primary/10">
                            {getStatusIcon(booking.status)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                                Booking #{booking.booking_id.slice(-6)}
                              </span>
                              <Badge className={getStatusColor(booking.status)}>
                                {booking.status}
                              </Badge>
                              {booking.payment_status === 'paid' && (
                                <Badge className="bg-green-500/20 text-green-400">Paid</Badge>
                              )}
                              {booking.has_insurance && (
                                <Badge className="bg-blue-500/20 text-blue-400">Insured</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {format(new Date(booking.start_time), 'MMM d, yyyy')}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {format(new Date(booking.start_time), 'h:mm a')} - {format(new Date(booking.end_time), 'h:mm a')}
                              </span>
                            </div>
                            
                            {/* Price Breakdown */}
                            <div className="mt-3 p-3 rounded-lg bg-muted/50 space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                  {booking.service_name || booking.service_category ? 
                                    `${booking.service_category || 'Service'} Services` : 'Service'}
                                </span>
                                <span>${booking.service_price?.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Platform Fee (8%)</span>
                                <span>${booking.platform_fee?.toFixed(2)}</span>
                              </div>
                              {booking.venue_fee > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Venue Fee (10%)</span>
                                  <span>${booking.venue_fee?.toFixed(2)}</span>
                                </div>
                              )}
                              {booking.tax_amount > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Tax (8.875%)</span>
                                  <span>${booking.tax_amount?.toFixed(2)}</span>
                                </div>
                              )}
                              {booking.insurance_fee > 0 && (
                                <div className="flex justify-between text-sm text-blue-400">
                                  <span>Booking Insurance (5%)</span>
                                  <span>${booking.insurance_fee?.toFixed(2)}</span>
                                </div>
                              )}
                              {booking.tip_amount > 0 && (
                                <div className="flex justify-between text-sm text-green-400">
                                  <span>Tip</span>
                                  <span>${booking.tip_amount?.toFixed(2)}</span>
                                </div>
                              )}
                              {booking.discount_amount > 0 && (
                                <div className="flex justify-between text-sm text-orange-400">
                                  <span>Discount ({booking.promo_code})</span>
                                  <span>-${booking.discount_amount?.toFixed(2)}</span>
                                </div>
                              )}
                              {booking.additional_fees_total > 0 && (
                                <div className="flex justify-between text-sm text-purple-400">
                                  <span>Additional Fees</span>
                                  <span>${booking.additional_fees_total?.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-bold pt-1 border-t border-border">
                                <span>Total</span>
                                <span className="text-primary">${booking.total_price?.toFixed(2)}</span>
                              </div>
                            </div>

                            {booking.notes && (
                              <p className="mt-2 text-sm text-muted-foreground">
                                Notes: {booking.notes}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {/* Add Fees Button - Available before payment */}
                          {booking.payment_status !== 'paid' && booking.status !== 'cancelled' && (
                            <Button
                              variant="outline"
                              onClick={() => openFeesDialog(booking)}
                              className="rounded-full"
                              data-testid={`fees-${booking.booking_id}`}
                            >
                              <Receipt className="h-4 w-4 mr-2" />
                              Add Fees
                              {booking.additional_fees?.filter(f => f.status === 'pending').length > 0 && (
                                <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 text-xs">
                                  {booking.additional_fees.filter(f => f.status === 'pending').length}
                                </Badge>
                              )}
                            </Button>
                          )}

                          {/* Payment Button for Clients */}
                          {!isTalent && booking.status === 'pending' && booking.payment_status !== 'paid' && (
                            <Button
                              onClick={() => openPaymentDialog(booking)}
                              disabled={paymentLoading}
                              className="rounded-full btn-glow"
                              data-testid={`pay-booking-${booking.booking_id}`}
                            >
                              <Wallet className="h-4 w-4 mr-2" />
                              Pay ${booking.total_price?.toFixed(2)}
                            </Button>
                          )}

                          {/* Contract Button */}
                          {(booking.status === 'confirmed' || booking.status === 'completed') && (
                            <Button
                              variant="outline"
                              onClick={() => openContractOptions(booking)}
                              className="rounded-full"
                              data-testid={`contract-${booking.booking_id}`}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Contract
                            </Button>
                          )}

                          {/* Talent Actions */}
                          {isTalent && booking.status === 'pending' && (
                            <>
                              <Button
                                onClick={() => handleUpdateStatus(booking.booking_id, 'confirmed')}
                                className="rounded-full"
                                data-testid={`confirm-${booking.booking_id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Confirm
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => handleUpdateStatus(booking.booking_id, 'cancelled')}
                                className="rounded-full"
                                data-testid={`cancel-${booking.booking_id}`}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Decline
                              </Button>
                            </>
                          )}

                          {/* Complete Button */}
                          {isTalent && booking.status === 'confirmed' && (
                            <Button
                              onClick={() => handleUpdateStatus(booking.booking_id, 'completed')}
                              className="rounded-full"
                              data-testid={`complete-${booking.booking_id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark Complete
                            </Button>
                          )}

                          {/* Leave Review Button - For Clients on Completed Bookings */}
                          {!isTalent && booking.status === 'completed' && !reviewedBookings.has(booking.booking_id) && (
                            <Button
                              variant="outline"
                              onClick={() => openReviewDialog(booking)}
                              className="rounded-full border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                              data-testid={`review-${booking.booking_id}`}
                            >
                              <Flame className="h-4 w-4 mr-2" />
                              Leave Review
                            </Button>
                          )}
                          {!isTalent && booking.status === 'completed' && reviewedBookings.has(booking.booking_id) && (
                            <Badge variant="outline" className="text-green-500 border-green-500/50">
                              <Check className="h-3 w-3 mr-1" />
                              Reviewed
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No {tab !== 'all' ? tab : ''} bookings</p>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              <Flame className="h-5 w-5 text-orange-500" />
              Leave a Review
            </DialogTitle>
          </DialogHeader>
          {selectedBookingForReview && (
            <div className="space-y-6 py-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">How was your experience?</p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((flame) => (
                    <button
                      key={flame}
                      type="button"
                      onClick={() => setReviewRating(flame)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`review-flame-${flame}`}
                    >
                      <Flame 
                        className={`h-8 w-8 transition-colors ${
                          flame <= reviewRating 
                            ? 'text-orange-500 fill-orange-500' 
                            : 'text-muted-foreground/30 hover:text-orange-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {reviewRating > 0 && (
                  <p className="text-sm text-primary mt-2">
                    {reviewRating === 1 && 'Poor'}
                    {reviewRating === 2 && 'Fair'}
                    {reviewRating === 3 && 'Good'}
                    {reviewRating === 4 && 'Great'}
                    {reviewRating === 5 && 'Excellent!'}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Write a review (optional)</Label>
                <Textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value.slice(0, 500))}
                  placeholder="Share your experience with this bookee..."
                  className="rounded-lg min-h-[120px]"
                  data-testid="review-text-input"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {reviewText.length}/500
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setReviewDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitReview}
                  disabled={reviewLoading || reviewRating === 0}
                  className="flex-1 rounded-xl"
                  data-testid="submit-review-btn"
                >
                  {reviewLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Flame className="h-4 w-4 mr-2" />
                  )}
                  Submit Review
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contract Dialog */}
      <Dialog open={contractDialogOpen} onOpenChange={(open) => {
        setContractDialogOpen(open);
        if (!open) {
          setSelectedBooking(null);
          setContractContent('');
          setContractType(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Syne, sans-serif' }}>
              Service Contract
              {contractType && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {contractType === 'ai' ? 'AI Generated' : 'Standard Template'}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {contractLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3">Generating contract...</span>
              </div>
            ) : contractContent ? (
              <div className="space-y-4">
                <div className="prose prose-invert max-w-none">
                  <pre className="whitespace-pre-wrap text-sm bg-muted/50 p-4 rounded-xl">
                    {contractContent}
                  </pre>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setContractContent('');
                      setContractType(null);
                    }}
                    className="rounded-full"
                  >
                    Generate Different Version
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(contractContent);
                      toast.success('Contract copied to clipboard');
                    }}
                    className="rounded-full"
                  >
                    Copy Contract
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-muted-foreground text-center mb-6">
                  Choose how you'd like to generate your contract:
                </p>
                <div className="grid gap-4">
                  {/* AI Generated Option */}
                  <button
                    onClick={() => handleGenerateContract(selectedBooking, false)}
                    className="p-4 rounded-xl border border-border hover:border-primary/50 bg-muted/30 hover:bg-muted/50 transition-all text-left group"
                    data-testid="contract-ai-option"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-primary/20 group-hover:bg-primary/30 transition-colors">
                        <Star className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">AI-Generated Contract</h3>
                        <p className="text-sm text-muted-foreground">
                          Custom contract created by AI based on your specific booking details, 
                          including personalized terms and conditions.
                        </p>
                        <Badge variant="secondary" className="mt-2 text-xs">Recommended</Badge>
                      </div>
                    </div>
                  </button>

                  {/* Static Template Option */}
                  <button
                    onClick={() => handleGenerateContract(selectedBooking, true)}
                    className="p-4 rounded-xl border border-border hover:border-primary/50 bg-muted/30 hover:bg-muted/50 transition-all text-left group"
                    data-testid="contract-template-option"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-muted group-hover:bg-muted/70 transition-colors">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Standard Template</h3>
                        <p className="text-sm text-muted-foreground">
                          Pre-written contract template with your booking details filled in. 
                          Quick and straightforward for common bookings.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  Both contract types include all relevant booking information and legal terms.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Line Items / Fees Dialog */}
      <Dialog open={feesDialogOpen} onOpenChange={setFeesDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Syne, sans-serif' }}>
              Additional Fees & Charges
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <p className="text-sm text-muted-foreground">
              Add extra charges like tips, travel expenses, equipment rentals, etc. 
              Each fee must be approved by the other party.
            </p>

            {/* Add New Fee Form */}
            <div className="p-4 rounded-xl bg-muted/50 space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add New Fee
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fee-name">Fee Name</Label>
                  <Input
                    id="fee-name"
                    placeholder="e.g., Tip, Travel, Equipment"
                    value={newFee.name}
                    onChange={(e) => setNewFee({ ...newFee, name: e.target.value })}
                    className="mt-1"
                    data-testid="fee-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="fee-amount">Amount ($)</Label>
                  <Input
                    id="fee-amount"
                    type="number"
                    step="0.01"
                    placeholder="50.00"
                    value={newFee.amount}
                    onChange={(e) => setNewFee({ ...newFee, amount: e.target.value })}
                    className="mt-1"
                    data-testid="fee-amount-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="fee-desc">Description (Optional)</Label>
                <Input
                  id="fee-desc"
                  placeholder="Brief description of the fee"
                  value={newFee.description}
                  onChange={(e) => setNewFee({ ...newFee, description: e.target.value })}
                  className="mt-1"
                />
              </div>
              <Button 
                onClick={handleAddFee} 
                disabled={addingFee}
                className="w-full rounded-xl"
                data-testid="add-fee-btn"
              >
                {addingFee ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Fee
              </Button>
            </div>

            {/* Existing Fees List */}
            {bookingFees.fees?.length > 0 ? (
              <div className="space-y-3">
                <h4 className="font-medium">Current Fees</h4>
                {bookingFees.fees.map((fee) => (
                  <div 
                    key={fee.fee_id} 
                    className={`p-4 rounded-xl border ${
                      fee.status === 'approved' ? 'border-green-500/30 bg-green-500/5' :
                      fee.status === 'rejected' ? 'border-red-500/30 bg-red-500/5' :
                      'border-yellow-500/30 bg-yellow-500/5'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{fee.name}</span>
                          <Badge className={getFeeStatusColor(fee.status)}>
                            {fee.status}
                          </Badge>
                        </div>
                        <p className="text-lg font-bold text-primary mt-1">${fee.amount.toFixed(2)}</p>
                        {fee.description && (
                          <p className="text-sm text-muted-foreground mt-1">{fee.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          Added by {fee.added_by_role} • {format(new Date(fee.created_at), 'MMM d, h:mm a')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {/* Show approve/reject buttons only for pending fees not created by current user */}
                        {fee.status === 'pending' && fee.added_by !== user?.user_id && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                              onClick={() => handleApproveFee(fee.fee_id, true)}
                              data-testid={`approve-fee-${fee.fee_id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={() => handleApproveFee(fee.fee_id, false)}
                              data-testid={`reject-fee-${fee.fee_id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {/* Show remove button only for pending fees created by current user */}
                        {fee.status === 'pending' && fee.added_by === user?.user_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => handleRemoveFee(fee.fee_id)}
                            data-testid={`remove-fee-${fee.fee_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Summary */}
                <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
                  <div className="flex justify-between text-sm">
                    <span>Approved Fees Total</span>
                    <span className="font-bold text-primary">${bookingFees.approved_total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span>Pending Approvals</span>
                    <span className="text-yellow-400">{bookingFees.pending_count}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">No additional fees added yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Method Selection Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Syne, sans-serif' }}>
              Choose Payment Method
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedBookingForPayment && (
              <div className="p-4 rounded-xl bg-muted/50">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Booking</span>
                  <span>#{selectedBookingForPayment.booking_id?.slice(-6)}</span>
                </div>
                {appliedPromo && (
                  <div className="flex justify-between text-sm text-green-400 mt-1">
                    <span>Discount ({appliedPromo.code})</span>
                    <span>-${appliedPromo.discount_applied?.toFixed(2)}</span>
                  </div>
                )}
                {tipAmount && tipAtCheckout && parseFloat(tipAmount) > 0 && (
                  <div className="flex justify-between text-sm text-green-400 mt-1">
                    <span>Tip (at checkout)</span>
                    <span>+${parseFloat(tipAmount).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg mt-2">
                  <span>Total</span>
                  <span className="text-primary">
                    ${((selectedBookingForPayment.total_price || 0) + (tipAtCheckout && tipAmount ? parseFloat(tipAmount) : 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Promo Code Section */}
            <div className="p-4 rounded-xl border border-dashed border-border">
              <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                <Tag className="h-4 w-4" />
                Have a promo code?
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter code"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase());
                    setPromoError('');
                  }}
                  disabled={!!appliedPromo || promoLoading}
                  className="flex-1 h-10 rounded-lg"
                  data-testid="promo-code-input"
                />
                <Button
                  variant="outline"
                  onClick={handleApplyPromo}
                  disabled={!promoCode.trim() || !!appliedPromo || promoLoading}
                  className="rounded-lg"
                  data-testid="apply-promo-btn"
                >
                  {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
              {promoError && (
                <p className="text-xs text-red-400 mt-1">{promoError}</p>
              )}
              {appliedPromo && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Code applied! You saved ${appliedPromo.discount_applied?.toFixed(2)}
                </p>
              )}
            </div>

            {/* Tip Section */}
            <div className="p-4 rounded-xl border border-dashed border-border">
              <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                💰 Add a Tip (Optional)
              </Label>
              <div className="flex gap-2 mb-2">
                {[10, 15, 20, 25].map(pct => {
                  const suggestedTip = (selectedBookingForPayment?.service_price * (pct / 100)).toFixed(2);
                  return (
                    <Button
                      key={pct}
                      variant={tipAmount === suggestedTip ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTipAmount(suggestedTip)}
                      className="flex-1 text-xs rounded-lg"
                    >
                      {pct}%
                    </Button>
                  );
                })}
              </div>
              <div className="flex gap-2 items-center">
                <span className="text-muted-foreground">$</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Custom amount"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="flex-1 h-9 rounded-lg"
                  data-testid="tip-amount-input"
                />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="tip-later"
                  checked={!tipAtCheckout}
                  onChange={(e) => setTipAtCheckout(!e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="tip-later" className="text-xs text-muted-foreground cursor-pointer">
                  Add tip later (within 24hrs after service)
                </label>
              </div>
              {!tipAtCheckout && (
                <p className="text-[10px] text-yellow-500 mt-1">
                  Note: Tips added after checkout incur a separate processing fee
                </p>
              )}
            </div>

            <div className="space-y-3">
              {paymentMethods.filter(m => m.available).map(method => (
                <button
                  key={method.id}
                  onClick={() => setSelectedPaymentMethod(method.id)}
                  disabled={!method.available}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${
                    selectedPaymentMethod === method.id
                      ? 'border-primary bg-primary/10'
                      : method.available
                        ? 'border-border hover:border-primary/50'
                        : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                  data-testid={`payment-method-${method.id}`}
                >
                  <div className="flex items-center gap-3">
                    {method.id === 'stripe' ? (
                      <CreditCard className="h-6 w-6 text-primary" />
                    ) : (
                      <PayPalIcon />
                    )}
                    <div>
                      <p className="font-medium">{method.name}</p>
                      <p className="text-sm text-muted-foreground">{method.description}</p>
                    </div>
                    {selectedPaymentMethod === method.id && (
                      <CheckCircle className="h-5 w-5 text-primary ml-auto" />
                    )}
                  </div>
                </button>
              ))}
              {paymentMethods.filter(m => m.available).length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-4">
                  No payment methods available. Please contact support.
                </p>
              )}
            </div>

            <Button
              onClick={handlePayment}
              disabled={paymentLoading || !selectedPaymentMethod || paymentMethods.filter(m => m.available).length === 0}
              className="w-full rounded-xl btn-glow"
              data-testid="confirm-payment-btn"
            >
              {paymentLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Wallet className="h-4 w-4 mr-2" />
              )}
              Pay ${selectedBookingForPayment?.total_price?.toFixed(2)}
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              Talent receives payment within 24-48 hours after service completion
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bookings;
